import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";

import {
  useCreateDraftCertificateMutation,
  useGetCertificateDataQuery,
} from "api/memberPortal/project/product/product.api";
import { useAuth } from "hooks/useAuth";
import { getCurrentDate } from "utils/timeUtils";

import cc_logo from "./CCLogo.gif";
import ccra_logo from "./CCRALogo.gif";
import watermark from "./DraftWaterMark.png";

import "./CertificateModal.css";

const CertificateModal = ({ show, handleClose, product_id, refetch }) => {
  const { authToken, csrfToken, currentUser, permissions } = useAuth();

  const { data: product } = useGetCertificateDataQuery(product_id, {
    skip: show === false,
  });
  const [triggerGenerateCertificate] = useCreateDraftCertificateMutation();

  const handleSubmit = (status) => {
    if (status === "Approve") {
      let data = {};

      if (permissions?.role_type === "Vendor") {
        data = {
          vendors_cc_draft_cert_verifier: currentUser.id,
          vendor_cc_draft_cert_date: getCurrentDate(),
        };
      } else {
        data = {
          labs_cc_draft_cert_verifier: currentUser.id,
          labs_cc_draft_cert_date: getCurrentDate(),
        };
      }

      axios
        .put(
          `${process.env.REACT_APP_DJANGO_ENDPOINT}project/product/${product?.product_id}/`,
          data,
          {
            withCredentials: true,
            headers: {
              "X-CSRFToken": csrfToken,
              Authorization: `Token ${authToken}`,
            },
          },
        )
        .then((response) => {
          if (
            response.data.vendors_cc_draft_cert_verifier &&
            response.data.labs_cc_draft_cert_verifier
          ) {
            triggerGenerateCertificate({ product_id: product_id });
          }
          refetch();
          handleClose();
        })
        .catch((error) => {
          console.log("error updating draft certificate", error);
        });
    } else if (status === "Reject") {
      axios
        .post(
          `${process.env.REACT_APP_DJANGO_ENDPOINT}alerts/alert_all_project/`,
          {
            alert_to_role: "NIAP",
            alert_text: `Product Certificate for ${product?.product_name} Rejected`,
            alert_on: getCurrentDate(),
            alert_type: "Product",
            alert_type_id: product?.product_id,
            active: true,
          },
          {
            withCredentials: true,
            headers: {
              "X-CSRFToken": csrfToken,
              Authorization: `Token ${authToken}`,
            },
          },
        )
        .then(() => handleClose())
        .catch((error) => console.log(error));
    }
  };

  return (
    <Modal size="xl" show={show} onHide={handleClose}>
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body>
        <div className="container-fluid p-5 certificate-data">
          <div
            style={{
              backgroundImage: `url(${watermark})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="row py-1">
              <div className="col-2 text-start">
                <img
                  style={{ width: "auto", height: 150, paddingLeft: 40, paddingRight: 40 }}
                  src={ccra_logo}
                  alt="Common Criteria Registration Authority"
                />
              </div>
              <div className="col-8 text-center">
                <p className="text-uppercase fw-bold m-0">
                  NATIONAL INFORMATION ASSURANCE PARTNERSHIP
                </p>
                <p className="h1 fw-bold m-0">Common Criteria Certificate</p>
                <p className="fst-italic m-0">is awarded to</p>
                <p className="h3 fw-bold m-0">{product?.vendor_id.name}</p>
                <p className="fst-italic m-0">for</p>
                <p className="h3 fw-bold m-0">{product?.product_name}</p>
              </div>
              <div className="col-2 text-center">
                <img
                  style={{ width: 200, height: "auto", paddingTop: 30, paddingBottom: 30, marginLeft: -50 }}
                  src={cc_logo}
                  alt="Common Criteria Portal"
                />
              </div>
            </div>
            <div className="row py-1">
              <div className="col" style={{ textAlign: "justify" }}>
                <p>
                  The IT product identified in this certificate has been
                  evaluated at an accredited testing laboratory using the Common
                  Methodology for IT Security Evaluation (Version 3.1) for
                  conformance to the Common Criteria for IT Security Evaluation
                  (Version 3.1). This certificate applies only to the specific
                  version and release of the product in its evaluated
                  configuration. The product’s functional and assurance security
                  specifications are contained in its security target. The
                  evaluation has been conducted in accordance with the
                  provisions of the NIAP Common Criteria Evaluation and
                  Validation Scheme and the conclusions of the testing
                  laboratory in the evaluation technical report are consistent
                  with the evidence adduced. This certificate is not an
                  endorsement of the IT product by any agency of the U.S.
                  Government and no warranty of the IT product is either
                  expressed or implied.
                </p>
              </div>
            </div>
            <div className="row py-1 data-items">
              <div className="col-7">
                <div className="row">
                  <div className="col">
                    <p className="fw-bold">Date Issued: {product?.certified_date}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <p className="fw-bold">
                      Validation Report Number:{" "}
                      {product?.validation_reports.length &&
                      product?.validation_reports.length > 0
                        ? product?.validation_reports[0].vr_number
                        : product?.default_vr_number}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <p className="fw-bold">CCTL: {product?.assigned_lab.name}</p>
                  </div>
                </div>
              </div>
              <div className="col-5">
                <div className="row">
                  <div className="col">
                    <p className="fw-bold">Assurance Level: PP Compliant</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <p className="fw-bold">Protection Profile Identifier:</p>
                    <ul>
                      {product?.protection_profiles.map((profile) => (
                        <li className="m-0" key={profile.pp_id}>
                          {profile.pp_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="row py-1">
              <div className="col-3"></div>
              <div className="col-6 text-center">
                <div style={{ borderBottom: "medium double black" }}>
                  <h1>&nbsp;</h1>
                </div>
                <p className="fst-italic small m-0">
                  Director, Common Criteria Evaluation and Validation Scheme
                </p>
                <p className="fw-bold text-uppercase small m-0">
                  NATIONAL INFORMATION ASSURANCE PARTNERSHIP
                </p>
              </div>
              <div className="col-3"></div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Row>
          <Col>
            <Button variant="light" onClick={handleClose}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button variant="danger" onClick={() => handleSubmit("Reject")}>
              Reject
            </Button>
          </Col>
          <Col>
            <Button variant="success" onClick={() => handleSubmit("Approve")}>
              Confirm
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default CertificateModal;
